import React from "react";
import CollapsibleCard from "../../../ForBrands/ui/CollapsibleCard";
import InsightList from "../../../ForBrands/ui/InsightList";
import { LegendRow } from "../../../ForBrands/ui/LegendItem";
import RankChip, { MedianChip, medianText, rankText } from "../../../ForBrands/ui/RankChip";
import { CHART } from "../../../ForBrands/ui/charts/chartTokens";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { PerfTile, PhoneLegend, SubHead, cx } from "./perfPieces";

// Skill Profile (Jayson's A2, spec B3 c; it takes the place of the
// profile's shooting efficiency rows): the key stats per 40 minutes and the
// efficiency figures in groups (a group of one or two metrics folded into
// the one before it), each tile with its "Top N%" rank (a stat that
// describes a role, usage or shot mix, shows its plain "88th pct" in the
// passive chip; "Not ranked" without a peer group), the division median
// ("Med 19.2") and "lower is better" where it applies, then the Skill read.
// Two groups a row on wide screens. Phones: two cells a row (an odd last
// cell alone), the ranks as text ("Top 12% · Med 19.2") as in the phone box
// score above, and the legend as plain lines. The desktop legend names each
// chip: the blue rank, the passive percentile of a role stat (how much, not
// how well) and the passive median, the passive ones with the profile's
// grey legend dot (the box score's "Peer group"). `view` is
// athletePerformanceModel.skillsView().

const DESKTOP_COLS = { 1: "sm:grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-4" };

// A metric's ranks as the phone cell prints them.
const phoneRanks = (m, approximate) => [
  { text: rankText(m.percentile, { kind: m.kind }), approximate: approximate && m.percentile !== null },
  medianText(m.median, { unit: m.unit }),
];

const SkillProfileCard = ({ view, approximate = false, cardProps = {} }) => {
  const phone = usePhoneTheme();
  const legend = [
    view.rankLegend && { key: "rank", swatch: "dot", color: "#2196f3", label: view.rankLegend },
    view.descriptiveLegend && { key: "descriptive", swatch: "dot", color: CHART.muted, label: view.descriptiveLegend },
    view.hasMedians && { key: "median", swatch: "dot", color: CHART.muted, label: "Division median" },
  ];
  return (
    <CollapsibleCard title="Skill Profile" subtitle={view.lede} {...cardProps}>
      <div className={phone ? "flex flex-col gap-4" : "grid gap-x-6 gap-y-5 lg:grid-cols-2"}>
        {view.groups.map((g) => (
          <div key={g.name} className="flex min-w-0 flex-col gap-2">
            <SubHead>{g.name}</SubHead>
            <div className={cx("grid gap-[10px]", phone ? "grid-cols-2" : cx("grid-cols-2", DESKTOP_COLS[Math.min(4, g.metrics.length)]))}>
              {g.metrics.map((m) => (
                <PerfTile
                  key={m.key}
                  value={m.value}
                  label={m.label}
                  note={m.lowerIsBetter ? "lower is better" : null}
                  ranks={phone ? phoneRanks(m, approximate) : null}
                >
                  <RankChip percentile={m.percentile} kind={m.kind} approximate={approximate} />
                  <MedianChip value={m.median} unit={m.unit} />
                </PerfTile>
              ))}
            </div>
          </div>
        ))}
      </div>
      {phone ? (
        <PhoneLegend lines={[[view.rankLegend, view.hasMedians && "Division median"].filter(Boolean).join(" · "), view.descriptiveLegend]} />
      ) : (
        <LegendRow items={legend} label="Skill Profile legend" />
      )}
      <InsightList insights={view.insights} variant="read" label="Skill read" />
    </CollapsibleCard>
  );
};

export default SkillProfileCard;
